"use client";

import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import {
  ArrowUpDown,
  ChevronDown,
  MoreHorizontal,
  Mail,
  User,
  Calendar,
  CreditCard,
  Trash2,
  Eye,
  ExternalLink,
  Building2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { toast } from "sonner";
import moment from "moment";
import {
  formatSubscriptionStatus,
  getSubscriptionStatusColor,
} from "@/utils/subscription-status";

// User type based on the API response
export type AdminUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  userType: "individual" | "company";
  companyName?: string;
  siret?: string;
  subscriptionPlan: string;
  subscriptionStatus: "active" | "inactive" | "pending";
  subscriptionEndDate?: string;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  customerId?: string;
  subscriptionId?: string;
};

interface UsersDataTableProps {
  data: AdminUser[];
  loading?: boolean;
  onViewUser?: (user: AdminUser) => void;
}

interface ActionsProps {
  user: AdminUser;
  onDeleteUser: (user: AdminUser) => void;
  onViewUser: (user: AdminUser) => void;
}

const Actions: React.FC<ActionsProps> = ({
  user,
  onDeleteUser,
  onViewUser,
}) => {
  const handleCopyId = () => {
    navigator.clipboard.writeText(user.id);
    toast.success("ID utilisateur copié dans le presse-papiers");
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(user.email);
    toast.success("Email copié dans le presse-papiers");
  };

  const handleOpenStripe = () => {
    if (!user.customerId) {
      toast.error("Aucun Customer ID Stripe disponible pour cet utilisateur");
      return;
    }

    const stripeUrl =
      import.meta.env.VITE_ENVIRONMENT === "development"
        ? `https://dashboard.stripe.com/test/customers/${user.customerId}`
        : `https://dashboard.stripe.com/customers/${user.customerId}`;
    window.open(stripeUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0" title="Actions">
          <span className="sr-only">Ouvrir le menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleCopyId}>
          <User className="mr-2 h-4 w-4" />
          Copier l'ID utilisateur
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleCopyEmail}>
          <Mail className="mr-2 h-4 w-4" />
          Copier l'email
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onViewUser(user)}
          className="cursor-pointer"
        >
          <Eye className="mr-2 h-4 w-4" />
          Voir les détails
        </DropdownMenuItem>

        {user.customerId && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleOpenStripe}
              className="cursor-pointer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Ouvrir dans Stripe
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => onDeleteUser(user)}
          className="cursor-pointer text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Supprimer définitivement
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export function UsersDataTable({
  data,
  loading,
  onViewUser,
}: UsersDataTableProps) {
  // Debug: Log all user data to see what's available

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<AdminUser | null>(
    null,
  );

  const handleDeleteUser = (user: AdminUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleViewUser = (user: AdminUser) => {
    if (onViewUser) {
      onViewUser(user);
    }
  };

  const createColumns = (): ColumnDef<AdminUser>[] => [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "email",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            <Mail className="h-4 w-4 mr-2" />
            Email
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center space-x-2">
            <div className="flex flex-col">
              <span className="font-medium text-gray-900 dark:text-white">
                {user.email}
              </span>
              {user.userType === "individual" &&
                (user.firstName || user.lastName) && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {`${user.firstName || ""} ${user.lastName || ""}`.trim()}
                  </span>
                )}
              {user.userType === "company" && user.companyName && (
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {user.companyName}
                </span>
              )}
            </div>
            {!user.isEmailVerified && (
              <Badge variant="destructive" className="text-xs">
                Non vérifié
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "userType",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            <User className="h-4 w-4 mr-2" />
            Type
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const user = row.original;
        const userType = user.userType;

        return (
          <div className="flex items-center space-x-2">
            {userType === "company" ? (
              <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            ) : (
              <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            )}
            <div className="flex flex-col">
              <Badge
                className={
                  userType === "company"
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                    : "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400"
                }
              >
                {userType === "company" ? "Entreprise" : "Particulier"}
              </Badge>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "customerId",
      header: () => <span>Stripe Customer ID</span>,
      cell: ({ row }) => {
        const customerId = row.getValue("customerId") as string;

        return (
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
            {customerId || "Aucun"}
          </span>
        );
      },
    },
    {
      accessorKey: "subscriptionId",
      header: () => <span>Stripe Subscription ID</span>,
      cell: ({ row }) => {
        const subscriptionId = row.getValue("subscriptionId") as string;

        return (
          <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
            {subscriptionId || "Aucun"}
          </span>
        );
      },
    },
    {
      accessorKey: "subscriptionPlan",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Plan
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const plan = row.getValue("subscriptionPlan") as string;

        // Function to generate color based on plan name
        const generatePlanColor = () => {
          if (!plan || plan.trim() === "")
            return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";

          const cleanedPlan = plan.trim().toLowerCase();

          // Explicit mapping for common plans to ensure consistency
          const planColorMap: Record<string, string> = {
            starter:
              "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400",
            pro: "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400",
            maxi: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400",
            premium:
              "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400",
            enterprise:
              "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
            basic:
              "bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-400",
            advanced:
              "bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400",
            ultimate:
              "bg-violet-100 text-violet-800 dark:bg-violet-900/20 dark:text-violet-400",
          };

          // Check if we have an explicit mapping first
          if (planColorMap[cleanedPlan]) {
            return planColorMap[cleanedPlan];
          }

          // Fallback to hash-based generation for unknown plans
          let hash = 0;
          for (let i = 0; i < cleanedPlan.length; i++) {
            hash = cleanedPlan.charCodeAt(i) + ((hash << 5) - hash);
          }

          // Generate color based on hash (excluding colors used for status badges: red, green, yellow)
          const colorMap = [
            "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400",
            "bg-stone-100 text-stone-800 dark:bg-stone-900/20 dark:text-stone-400",
            "bg-zinc-100 text-zinc-800 dark:bg-zinc-900/20 dark:text-zinc-400",
            "bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400",
          ];

          return colorMap[Math.abs(hash) % colorMap.length];
        };

        return (
          <Badge className={generatePlanColor()}>{plan || "Non abonné"}</Badge>
        );
      },
    },
    {
      accessorKey: "subscriptionStatus",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Statut
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.getValue("subscriptionStatus") as string;

        return (
          <Badge className={getSubscriptionStatusColor(status)}>
            {formatSubscriptionStatus(status)}
          </Badge>
        );
      },
    },

    {
      accessorKey: "createdAt",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Inscription
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as string;
        return (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {moment(date).format("DD/MM/YYYY")}
          </div>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const user = row.original;
        return (
          <Actions
            user={user}
            onDeleteUser={handleDeleteUser}
            onViewUser={handleViewUser}
          />
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns: createColumns(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  if (loading) {
    return (
      <div className="w-full">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Stripe Customer ID</TableHead>
                <TableHead>Stripe Subscription ID</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Inscription</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  <TableCell>
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        <Input
          placeholder="Filtrer par email..."
          value={(table.getColumn("email")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("email")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Colonnes <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id === "email"
                      ? "Email"
                      : column.id === "userType"
                        ? "Type d'utilisateur"
                        : column.id === "customerId"
                          ? "Customer ID"
                          : column.id === "subscriptionId"
                            ? "Subscription ID"
                            : column.id === "subscriptionPlan"
                              ? "Abonnement"
                              : column.id === "subscriptionStatus"
                                ? "Statut"
                                : column.id === "createdAt"
                                  ? "Inscription"
                                  : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={createColumns().length}
                  className="h-24 text-center"
                >
                  <div className="flex flex-col items-center justify-center py-12">
                    <User className="h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Aucun utilisateur trouvé
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} sur{" "}
          {table.getFilteredRowModel().rows.length} ligne(s) sélectionnée(s).
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Suivant
          </Button>
        </div>
      </div>

      <DeleteUserDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        user={selectedUser}
        onSuccess={() => {
          // Optionally refresh the data here
        }}
      />
    </div>
  );
}
